import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { Skeleton } from "@/components/Skeleton";
import { Button } from "@/components/Button";
import { PriorityChip } from "@/components/PriorityChip";
import type { RequirementRow } from "@shared/types";

const COVERAGE_META: Record<string, { label: string; cls: string }> = {
  none: { label: "Unaddressed", cls: "cov-none" },
  partial: { label: "In progress", cls: "cov-partial" },
  done: { label: "Done", cls: "cov-done" },
};

function RequirementsView() {
  const { projectId } = Route.useParams();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [priority, setPriority] = useState("3");
  const [expanded, setExpanded] = useState<string | null>(null);

  const reqQ = useQuery({
    queryKey: queryKeys.requirements(projectId),
    queryFn: () => api.listRequirements(projectId),
  });
  const tasksQ = useQuery({
    queryKey: queryKeys.tasksFor(projectId),
    queryFn: () => api.listTasks(projectId),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: queryKeys.requirements(projectId) });

  const create = useMutation({
    mutationFn: () =>
      api.createRequirement(projectId, {
        title: title.trim(),
        priority: Number(priority),
      }),
    onSuccess: () => {
      invalidate();
      setTitle("");
    },
  });

  const reqs = reqQ.data?.requirements ?? [];
  const unaddressed = reqs.filter((r) => r.coverage === "none").length;

  return (
    <div className="proj-shell-content">
      <div className="rpt-head" style={{ marginBottom: 6 }}>
        <h2 className="blk-h" style={{ margin: 0 }}>
          Requirements
          {reqs.length > 0 && (
            <span className="t-sm t-muted" style={{ marginLeft: 8 }}>
              {reqs.length - unaddressed}/{reqs.length} addressed
            </span>
          )}
        </h2>
      </div>
      <p className="t-sm t-muted">
        Track requirements and link the tasks that implement them. Coverage
        rolls up from those tasks; unaddressed requirements are flagged.
      </p>

      <div className="req-new">
        <input
          className="input"
          placeholder="New requirement…"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          aria-label="Requirement title"
          style={{ flex: 1, minWidth: 0 }}
        />
        <select
          className="select"
          value={priority}
          onChange={(e) => setPriority(e.target.value)}
          aria-label="Requirement priority"
          // The base .select is width:100%, which starved the flex:1 title
          // input to ~0. Pin it and stop it shrinking the input.
          style={{ width: 150, flexShrink: 0 }}
        >
          <option value="1">Urgent</option>
          <option value="2">High</option>
          <option value="3">Medium</option>
          <option value="4">Low</option>
        </select>
        <Button
          variant="primary"
          disabled={!title.trim() || create.isPending}
          onClick={() => create.mutate()}
        >
          Add
        </Button>
      </div>

      {reqQ.isLoading ? (
        <Skeleton count={5} height={40} />
      ) : reqs.length === 0 ? (
        <p className="t-muted" style={{ marginTop: 16 }}>
          No requirements yet.
        </p>
      ) : (
        <table className="req-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Requirement</th>
              <th>Priority</th>
              <th>Coverage</th>
              <th>Tasks</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {reqs.map((r) => (
              <RequirementRowView
                key={r.id}
                req={r}
                tasks={tasksQ.data?.tasks ?? []}
                expanded={expanded === r.id}
                onToggle={() => setExpanded((e) => (e === r.id ? null : r.id))}
                onChange={invalidate}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RequirementRowView({
  req,
  tasks,
  expanded,
  onToggle,
  onChange,
}: {
  req: RequirementRow;
  tasks: { id: string; title: string; status: string }[];
  expanded: boolean;
  onToggle: () => void;
  onChange: () => void;
}) {
  const qc = useQueryClient();
  const cov = COVERAGE_META[req.coverage] ?? COVERAGE_META.none!;
  const linkedQ = useQuery({
    queryKey: ["requirement-tasks", req.id],
    queryFn: () => api.requirementTasks(req.id),
    enabled: expanded,
  });
  const linkedIds = useMemo(
    () => new Set((linkedQ.data?.tasks ?? []).map((t) => t.taskId)),
    [linkedQ.data],
  );

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["requirement-tasks", req.id] });
    onChange();
  };
  const link = useMutation({
    mutationFn: (taskId: string) => api.linkRequirementTask(req.id, taskId),
    onSuccess: refresh,
  });
  const unlink = useMutation({
    mutationFn: (taskId: string) => api.unlinkRequirementTask(req.id, taskId),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: () => api.deleteRequirement(req.id),
    onSuccess: onChange,
  });

  return (
    <>
      <tr className={req.coverage === "none" ? "req-row-unaddressed" : ""}>
        <td className="t-mono">REQ-{String(req.reqNumber).padStart(3, "0")}</td>
        <td>
          <button type="button" className="req-title-btn" onClick={onToggle}>
            {req.title}
          </button>
        </td>
        <td>
          <PriorityChip level={req.priority} />
        </td>
        <td>
          <span className={`cov-badge ${cov.cls}`}>{cov.label}</span>
        </td>
        <td className="t-sm t-muted">
          {req.doneCount}/{req.taskCount}
        </td>
        <td>
          <button
            type="button"
            className="chip-btn"
            onClick={() => {
              if (confirm(`Delete REQ-${req.reqNumber}?`)) remove.mutate();
            }}
          >
            Delete
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="req-detail">
            <div className="req-linked">
              <strong className="t-sm">Implementing / verifying tasks</strong>
              {linkedQ.isLoading ? (
                <span className="t-sm t-muted"> loading…</span>
              ) : (linkedQ.data?.tasks ?? []).length === 0 ? (
                <span className="t-sm t-muted"> none linked yet</span>
              ) : (
                <ul className="req-linked-list">
                  {(linkedQ.data?.tasks ?? []).map((t) => (
                    <li key={t.taskId}>
                      <span className={t.status === "done" ? "t-done" : ""}>
                        {t.status === "done" ? "✓ " : "○ "}
                        {t.title}
                      </span>
                      <button
                        type="button"
                        className="chip-btn"
                        onClick={() => unlink.mutate(t.taskId)}
                      >
                        Unlink
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="req-link-add">
                <select
                  className="select"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) link.mutate(e.target.value);
                  }}
                  aria-label={`Link a task to REQ-${req.reqNumber}`}
                >
                  <option value="">Link a task…</option>
                  {tasks
                    .filter((t) => !linkedIds.has(t.id))
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export const Route = createFileRoute("/projects/$projectId/requirements")({
  component: RequirementsView,
});
